// Freight rate card and customs duty schedule.
//
// ⚠️ BOTH TABS READ THE DATABASE. They rendered mocks/freightRates.ts and
// mocks/dutyRates.ts until this slice, while anixify.quote.line resolved an
// imported line's duty out of the REAL anixify.duty.rate table. A rate shown
// on this screen and the rate used in a price were different numbers, and
// nothing on either side could reveal the disagreement.
//
// ⚠️ FAIL LOUD, NEVER FALL BACK. The rates ARE this screen. A failed read
// clears the rows, shows the backend's reason with a Retry, and the table is
// not rendered at all -- an empty grid under a filter bar reads as "nothing
// matched". A mock fallback here would be worse than anywhere else in the
// app: it would show a plausible rate the pricer is not using.
//
// ⚠️ READ-ONLY. There is no write endpoint for either table, and the two
// detail panels keep their disabled ReadOnlySave. The "Add Rate" buttons
// that used to sit in both filter bars were indigo primaries with no
// onClick -- they are gone, and the read-only note in each panel is where
// the honest statement lives now.
//
// ⚠️ EVERY FILTER OPTION IS DERIVED FROM THE LOADED ROWS. Origin, Mode and
// Category were literal arrays; a hardcoded list goes stale in the silent
// direction (see lib/filterOptions.ts, and /catalog's missing "Poly"). Status
// is the one exception and is deliberate: it is not a column of the data but
// a verdict computed from `active` and the dates by statusOf, so its three
// values are the function's own range rather than anything a row holds.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Search, AlertTriangle, Info } from "lucide-react";
import { listFreightRates, listDutyRates } from "@/services/api";
import { FreightRatesTable, statusOf } from "@/components/FreightRatesTable";
import { FreightRateDetailPanel } from "@/components/FreightRateDetailPanel";
import { DutyRatesTable, statusOf as dutyStatusOf } from "@/components/DutyRatesTable";
import { DutyRateDetailPanel } from "@/components/DutyRateDetailPanel";
import {
  ReferenceLoading, ReferenceError, ReferenceEmpty, type LoadStatus,
} from "@/components/ReferenceDataStates";
import { derivedOptions } from "@/lib/filterOptions";
import type { FreightRate, DutyRate } from "@/types";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke } from "@/styles/tokens";

type Tab = "freight" | "duty";

// NOT derived, and that is the point: these are the RANGE OF statusOf, a
// verdict computed from `active` plus the dates, not a column any row holds.
// Deriving them from the rows would mean the filter silently lost "Expired"
// on a day when nothing happened to be expired -- which is exactly when
// somebody would want to check.
const STATUS_OPTS = ["All", "Active", "Expiring Soon", "Expired"];
const DUTY_STATUS_OPTS = ["All", "Active", "Superseded"];

const SELECT_STYLE: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, fontSize: typography.fontSize.base,
  color: semanticColors.textPrimary, backgroundColor: semanticColors.surface,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  outline: "none",
};

export default function SourcingPage() {
  const [tab, setTab] = useState<Tab>("freight");

  const [rates, setRates] = useState<FreightRate[]>([]);
  const [freightStatus, setFreightStatus] = useState<LoadStatus>("loading");
  const [freightError, setFreightError] = useState("");

  const [dutyRates, setDutyRates] = useState<DutyRate[]>([]);
  const [dutyLoadStatus, setDutyLoadStatus] = useState<LoadStatus>("loading");
  const [dutyError, setDutyError] = useState("");

  const [search, setSearch]   = useState("");
  const [origin, setOrigin]   = useState<string>("All");
  const [mode, setMode]       = useState<string>("All");
  const [status, setStatus]   = useState<string>("All");
  const [warnFilter, setWarnFilter] = useState(false);
  const [selected, setSelected]     = useState<FreightRate | null>(null);

  const [dutySearch, setDutySearch]     = useState("");
  const [dutyCategory, setDutyCategory] = useState<string>("All");
  const [dutyStatus, setDutyStatus]     = useState<string>("All");
  const [selectedDuty, setSelectedDuty] = useState<DutyRate | null>(null);

  const loadFreight = useCallback(async () => {
    setFreightStatus("loading");
    setFreightError("");
    const result = await listFreightRates();
    if (!result.ok) {
      // Cleared, not left stale. Rows under a failed refresh imply they are
      // current, and these rows are rates somebody may be about to quote off.
      setRates([]);
      setSelected(null);
      setFreightError(result.error ?? "Backend reported an unknown failure.");
      setFreightStatus("error");
      return;
    }
    setRates(result.rates ?? []);
    setFreightStatus("ok");
  }, []);

  const loadDuty = useCallback(async () => {
    setDutyLoadStatus("loading");
    setDutyError("");
    const result = await listDutyRates();
    if (!result.ok) {
      setDutyRates([]);
      setSelectedDuty(null);
      setDutyError(result.error ?? "Backend reported an unknown failure.");
      setDutyLoadStatus("error");
      return;
    }
    setDutyRates(result.rates ?? []);
    setDutyLoadStatus("ok");
  }, []);

  // BOTH ON MOUNT, not per tab. The tabs are two views of one screen and
  // switching between them must not stall on a fresh request; two small
  // bounded reads cost less than the flicker would.
  useEffect(() => { loadFreight(); }, [loadFreight]);
  useEffect(() => { loadDuty(); }, [loadDuty]);

  // ---- derived filter options --------------------------------------------
  const originOptions   = useMemo(() => derivedOptions(rates, (r) => r.origin), [rates]);
  const modeOptions     = useMemo(() => derivedOptions(rates, (r) => r.mode), [rates]);
  const categoryOptions = useMemo(() => derivedOptions(dutyRates, (r) => r.category), [dutyRates]);

  // A selection the loaded rows can no longer satisfy would filter the table
  // to nothing and look like an empty result. Reset it instead.
  useEffect(() => {
    if (origin !== "All" && !originOptions.includes(origin)) setOrigin("All");
  }, [originOptions, origin]);
  useEffect(() => {
    if (mode !== "All" && !modeOptions.includes(mode)) setMode("All");
  }, [modeOptions, mode]);
  useEffect(() => {
    if (dutyCategory !== "All" && !categoryOptions.includes(dutyCategory)) setDutyCategory("All");
  }, [categoryOptions, dutyCategory]);

  const counts = useMemo(() => {
    let exp = 0, soon = 0;
    for (const r of rates) {
      const s = statusOf(r);
      if (s === "Expired") exp++;
      else if (s === "Expiring Soon") soon++;
    }
    return { expired: exp, soon };
  }, [rates]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rates.filter((r) => {
      if (origin !== "All" && r.origin !== origin) return false;
      if (mode   !== "All" && r.mode   !== mode)   return false;
      const s = statusOf(r);
      if (status !== "All" && s !== status) return false;
      if (warnFilter && s === "Active") return false;
      if (q.length > 0) {
        const hay = `${r.origin} ${r.carrier}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rates, search, origin, mode, status, warnFilter]);

  const filteredDuty = useMemo(() => {
    const q = dutySearch.trim().toLowerCase();
    return dutyRates.filter((r) => {
      if (dutyCategory !== "All" && r.category !== dutyCategory) return false;
      const s = dutyStatusOf(r);
      if (dutyStatus !== "All" && s !== dutyStatus) return false;
      if (q.length > 0) {
        const hay = `${r.hsnCode} ${r.description}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [dutyRates, dutySearch, dutyCategory, dutyStatus]);

  return (
    <div data-testid="sourcing-page" className="flex" style={{ height: "calc(100vh - 48px)" }}>
      <div className="flex flex-col" style={{ padding: spacing[24], flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: spacing[16] }}>
          <div data-testid="sourcing-title"
               style={{ fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight }}>
            Sourcing
          </div>
          <div data-testid="sourcing-subtitle"
               style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4] }}>
            Published freight routes and customs duty rates. Read-only in this build.
          </div>
        </div>

        <div data-testid="sourcing-tabs"
             className="flex items-center"
             style={{ borderBottom: `1px solid ${semanticColors.border}`, marginBottom: spacing[16], gap: spacing[24] }}>
          {(["freight", "duty"] as Tab[]).map((t) => {
            const active = tab === t;
            const label = t === "freight" ? "Freight Rates" : "Duty Rates";
            return (
              <button key={t} data-testid={`tab-${t}`} data-active={active ? "true" : "false"}
                      onClick={() => setTab(t)}
                      style={{
                        background: "transparent", border: "none",
                        padding: `${spacing[10]}px ${spacing[2]}px`, cursor: "pointer",
                        fontSize: typography.fontSize.base,
                        color: active ? semanticColors.textPrimary : semanticColors.textMuted,
                        fontWeight: active ? typography.fontWeight.medium : typography.fontWeight.regular,
                        borderBottom: `2px solid ${active ? colors.indigo600 : "transparent"}`,
                        marginBottom: "-1px",
                      }}>
                {label}
              </button>
            );
          })}
        </div>

        {tab === "duty" ? (
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {/* Info line */}
            <div
              data-testid="duty-info-bar"
              className="flex items-center"
              style={{
                minHeight: heights.control, padding: `${spacing[6]}px ${spacing[12]}px`, marginBottom: spacing[12], flexShrink: 0,
                backgroundColor: colors.indigo50,
                border: `1px solid ${colors.indigo100}`, borderRadius: radius.base,
                gap: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
              }}
            >
              <Info size={iconSize.md} strokeWidth={iconStroke.regular} color={colors.indigo600} />
              <span>
                Indian import duty builds in order: BCD on the goods value, then SWS at 10% of the BCD, then IGST on the total.
                IGST is recoverable as input credit and is not counted in landed cost or margin.
              </span>
            </div>

            {dutyLoadStatus === "error" && (
              <ReferenceError
                testId="duty-load-error"
                retryTestId="duty-retry"
                message="Could not load the duty schedule."
                detail={dutyError}
                onRetry={loadDuty}
              />
            )}

            {dutyLoadStatus === "loading" && (
              <ReferenceLoading testId="duty-loading" label="Loading duty rates…" />
            )}

            {dutyLoadStatus === "ok" && dutyRates.length === 0 && (
              <ReferenceEmpty
                testId="duty-empty"
                label="No duty rates are on file. The schedule is maintained in Odoo."
              />
            )}

            {dutyLoadStatus === "ok" && dutyRates.length > 0 && (
              <>
                {/* Filter bar */}
                <div
                  data-testid="duty-filter-bar"
                  className="flex items-center"
                  style={{
                    height: heights.filterBar, padding: `0 ${spacing[12]}px`, marginBottom: spacing[12],
                    backgroundColor: semanticColors.panelBg,
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    gap: spacing[12],
                  }}
                >
                  <div
                    className="flex items-center"
                    style={{
                      flex: 1, minWidth: layout.searchMinWidth,
                      height: heights.input, padding: `0 ${spacing[8]}px`,
                      backgroundColor: semanticColors.surface,
                      border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                      gap: spacing[6],
                    }}
                  >
                    <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
                    <input
                      data-testid="duty-search"
                      value={dutySearch}
                      onChange={(e) => setDutySearch(e.target.value)}
                      placeholder="Search HSN code or description"
                      style={{
                        flex: 1, minWidth: 0, height: "100%",
                        fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                        background: "transparent", border: "none", outline: "none",
                      }}
                    />
                  </div>
                  {/* Hidden when the loaded rows carry no grouping at all --
                      a dropdown holding only "All" invites a click that can
                      change nothing. */}
                  {categoryOptions.length > 0 && (
                    <select data-testid="duty-filter-category" value={dutyCategory}
                            onChange={(e) => setDutyCategory(e.target.value)} style={SELECT_STYLE}>
                      <option value="All">Category: All</option>
                      {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                  <select data-testid="duty-filter-status" value={dutyStatus}
                          onChange={(e) => setDutyStatus(e.target.value)} style={SELECT_STYLE}>
                    {DUTY_STATUS_OPTS.map((s) => <option key={s} value={s}>{s === "All" ? "Status: All" : s}</option>)}
                  </select>
                </div>

                <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[8] }}>
                  <span data-testid="duty-filtered-count" className="tabular-nums">{filteredDuty.length}</span>
                  {filteredDuty.length !== dutyRates.length ? (
                    <> of <span className="tabular-nums">{dutyRates.length}</span> rates</>
                  ) : (
                    <> rates</>
                  )}
                </div>

                <DutyRatesTable
                  rows={filteredDuty}
                  onRowClick={setSelectedDuty}
                  selectedId={selectedDuty?.id}
                />
              </>
            )}
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {freightStatus === "error" && (
              <ReferenceError
                testId="freight-load-error"
                retryTestId="freight-retry"
                message="Could not load the freight rate card."
                detail={freightError}
                onRetry={loadFreight}
              />
            )}

            {freightStatus === "loading" && (
              <ReferenceLoading testId="freight-loading" label="Loading freight rates…" />
            )}

            {freightStatus === "ok" && rates.length === 0 && (
              <ReferenceEmpty
                testId="freight-empty"
                label="No freight rates are on file. The rate card is maintained in Odoo."
              />
            )}

            {freightStatus === "ok" && rates.length > 0 && (
              <>
                {/* Warning strip */}
                {(counts.expired > 0 || counts.soon > 0) && (
                  <div
                    data-testid="freight-warn-strip"
                    className="flex items-center justify-between"
                    style={{
                      minHeight: heights.control, padding: `${spacing[6]}px ${spacing[12]}px`, marginBottom: spacing[12], flexShrink: 0,
                      backgroundColor: colors.amber50,
                      border: `1px solid ${colors.amber200}`, borderRadius: radius.base,
                      gap: spacing[8],
                    }}
                  >
                    <div className="flex items-center" style={{ gap: spacing[8], fontSize: typography.fontSize.sm, color: colors.amber800 }}>
                      <AlertTriangle size={iconSize.md} strokeWidth={iconStroke.regular} color={colors.amber600} />
                      <span className="tabular-nums">
                        {counts.expired} rate{counts.expired === 1 ? "" : "s"} expired, {counts.soon} expiring within 7 days
                      </span>
                    </div>
                    <button
                      data-testid="warn-strip-filter"
                      data-active={warnFilter ? "true" : "false"}
                      onClick={() => setWarnFilter((v) => !v)}
                      style={{
                        background: "transparent", border: "none",
                        color: colors.amber600, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
                        cursor: "pointer", padding: 0,
                      }}
                    >
                      {warnFilter ? "Show all" : "Show only these"}
                    </button>
                  </div>
                )}

                {/* Filter bar */}
                <div
                  data-testid="freight-filter-bar"
                  className="flex items-center"
                  style={{
                    height: heights.filterBar, padding: `0 ${spacing[12]}px`, marginBottom: spacing[12],
                    backgroundColor: semanticColors.panelBg,
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    gap: spacing[12],
                  }}
                >
                  <div
                    className="flex items-center"
                    style={{
                      flex: 1, minWidth: layout.searchMinWidth,
                      height: heights.input, padding: `0 ${spacing[8]}px`,
                      backgroundColor: semanticColors.surface,
                      border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                      gap: spacing[6],
                    }}
                  >
                    <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
                    <input
                      data-testid="freight-search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search origin or carrier"
                      style={{
                        flex: 1, minWidth: 0, height: "100%",
                        fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                        background: "transparent", border: "none", outline: "none",
                      }}
                    />
                  </div>
                  {originOptions.length > 0 && (
                    <select data-testid="freight-filter-origin" value={origin}
                            onChange={(e) => setOrigin(e.target.value)} style={SELECT_STYLE}>
                      <option value="All">Origin: All</option>
                      {originOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  )}
                  {modeOptions.length > 0 && (
                    <select data-testid="freight-filter-mode" value={mode}
                            onChange={(e) => setMode(e.target.value)} style={SELECT_STYLE}>
                      <option value="All">Mode: All</option>
                      {modeOptions.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  )}
                  <select data-testid="freight-filter-status" value={status}
                          onChange={(e) => setStatus(e.target.value)} style={SELECT_STYLE}>
                    {STATUS_OPTS.map((s) => <option key={s} value={s}>{s === "All" ? "Status: All" : s}</option>)}
                  </select>
                </div>

                <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[8] }}>
                  <span data-testid="freight-filtered-count" className="tabular-nums">{filtered.length}</span>
                  {filtered.length !== rates.length ? (
                    <> of <span className="tabular-nums">{rates.length}</span> rates</>
                  ) : (
                    <> rates</>
                  )}
                </div>

                <FreightRatesTable
                  rows={filtered}
                  onRowClick={setSelected}
                  selectedId={selected?.id}
                />
              </>
            )}
          </div>
        )}
      </div>

      {tab === "freight" && selected && (
        <FreightRateDetailPanel rate={selected} onClose={() => setSelected(null)} />
      )}
      {tab === "duty" && selectedDuty && (
        <DutyRateDetailPanel rate={selectedDuty} onClose={() => setSelectedDuty(null)} />
      )}
    </div>
  );
}
